import { Stack, TextFieldProps } from '@mui/material';
import TextField from '@mui/material/TextField';
import { Controller, FieldPath, FieldValues, PathValue, useFormContext } from 'react-hook-form';
import { PTypography } from '@/components/atoms/Typography/PTypography';
import { applyWarningSx } from '@/components/hook-form/utils';
import WarningHelperText from '@/components/hook-form/WarningHelperText';

type Props<TFieldValues extends FieldValues = FieldValues> = TextFieldProps & {
  name: FieldPath<TFieldValues>;
  defaultValue?: PathValue<TFieldValues, FieldPath<TFieldValues>>;
  disabled?: boolean;
  /**
   * Setting this will show the character count at the end of the helper text section and the character limit. If you
   * use this you can not set the helperText prop yourself.
   */
  characterLimit?: number;
  onValueChange?: (value: string) => void;
  warningText?: string;
};

/**
 * Minimal@4 component.
 */
export default function RHFTextField<TFieldValues extends FieldValues = FieldValues>({
  name,
  helperText,
  defaultValue,
  disabled = false,
  characterLimit = 0,
  onValueChange,
  warningText,
  sx,
  ...other
}: Props<TFieldValues>) {
  const { control, getValues } = useFormContext<TFieldValues>();
  const effectiveSx = applyWarningSx(warningText, sx);

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ field: { ref, onChange, ...field }, fieldState: { error } }) => (
        <TextField
          {...field}
          fullWidth
          error={!!error}
          disabled={disabled}
          inputRef={ref}
          sx={effectiveSx}
          helperText={
            helperText || warningText || error?.message || characterLimit > 0 ? (
              <Stack spacing={1} direction="row" justifyContent="space-between">
                {!error && warningText ? (
                  <WarningHelperText text={warningText} />
                ) : !error && helperText ? (
                  <PTypography variant="caption" color="secondary">
                    {helperText}
                  </PTypography>
                ) : null}
                {error?.message ? (
                  <PTypography variant="caption" color="error">
                    {error.message}
                  </PTypography>
                ) : null}
                {characterLimit > 0 ? (
                  <PTypography variant="caption" color="secondary">
                    {`${getValues(name)?.length ?? 0}/${characterLimit}`}
                  </PTypography>
                ) : null}
              </Stack>
            ) : null
          }
          onChange={(event) => {
            const value = event.target.value;
            onChange(value);
            onValueChange?.(value);
          }}
          {...other}
        />
      )}
    />
  );
}
